// Instant answers in the chat (owner decision 2026-09-27, "Option C"): status questions answered at once from the latest
// published data, with no agent. Pure functions over the engagement's workspace tables (wp_index, attention, pbc_index,
// wp_messages) and team: they select, group and count the engine's rows — every state word is the engine's (rule 9) — and
// every line links to the workpaper, client document or action item it is about. Nothing is posted to the engine or kept.
import type { AttentionItem, PbcIndexRow, TeamMember, WpIndexRow, WpMessage } from "../data/types";
import { fmtDate } from "./format";
import { PHASES, age, nextItems, phaseSummary, subkindLabel, waitsOnMe, workPhase } from "./workspace";

export type ChatScopeKind = "workpaper" | "engagement" | "pbc";
export type ChipId =
  | "blocking" | "approvals" | "late_docs" | "phases" | "next"
  | "why_blocked" | "left" | "signs_next"
  | "pbc_late" | "pbc_failed" | "pbc_outstanding";

export const QUICK_CHIPS: Record<ChatScopeKind, Array<{ id: ChipId; label: string }>> = {
  engagement: [
    { id: "blocking", label: "What's blocking?" }, { id: "approvals", label: "What needs my approval?" }, { id: "late_docs", label: "Late client documents" },
    { id: "phases", label: "Progress by phase" }, { id: "next", label: "What's next?" },
  ],
  workpaper: [{ id: "why_blocked", label: "Why is it blocked?" }, { id: "left", label: "What's left?" }, { id: "signs_next", label: "Who signs next?" }],
  pbc: [{ id: "pbc_late", label: "Which are late?" }, { id: "pbc_failed", label: "Which failed processing?" }, { id: "pbc_outstanding", label: "Outstanding by type" }],
};

/** One line of an answer: always a link to what it is about; `also` links to what it waits on. */
export interface QaLine { key: string; text: string; href: string; detail?: string; badge?: string; also?: { label: string; href: string } }
export interface QaGroup { title: string | null; lines: QaLine[] }
export interface QuickAnswer { chip: ChipId; question: string; summary: string; groups: QaGroup[]; empty: boolean; asOf: string | null }

/** What an answer is computed from: one engagement's published tables, the viewer and today's date (YYYY-MM-DD). */
export interface QaData {
  id: string; syncedAt: string | null; progress: { final: number; total: number } | null;
  wp: WpIndexRow[]; attention: AttentionItem[]; pbc: PbcIndexRow[]; messages: WpMessage[]; team: TeamMember[];
  me: string; today: string; now?: number;
}

// ── links ──
export const wpHref = (id: string, ref: string) => `/e/${id}/wp/${encodeURIComponent(ref)}`;
export const docHref = (id: string, pbcId: string) => `/e/${id}/documents#pbc-${pbcId}`;
export const phaseHref = (id: string, code: string) => `/e/${id}/folders/${code}`;
/** An attention item's own place: its workpaper, its client document, else its Action center tab. */
export function itemHref(i: AttentionItem): string {
  const p = i.action.payload;
  const doc = p.pbc ?? (i.tab === "client_request" ? p.id : undefined);
  if (i.ref) return wpHref(i.engagementId, i.ref);
  if (doc !== undefined && doc !== null) return docHref(i.engagementId, String(doc));
  if (typeof p.ref === "string") return wpHref(i.engagementId, p.ref);
  return `/e/${i.engagementId}/actions/${i.tab}`;
}
/** Where a workpaper's `waiting_on` points: the client document, the other workpaper, or the decision's item. */
function waitHref(d: QaData, w: NonNullable<WpIndexRow["waitingOn"]>): string {
  if (w.kind === "client") {
    const doc = d.pbc.find((p) => p.ref === w.ref || p.id === w.ref || `PBC-${p.id}` === w.ref);
    return doc ? docHref(d.id, doc.id) : `/e/${d.id}/documents`;
  }
  if (w.kind === "workpaper") return wpHref(d.id, w.ref);
  const item = d.attention.find((a) => a.key === w.ref);
  return item ? itemHref(item) : `/e/${d.id}/actions/question`;
}
const WAIT_WORD: Record<string, string> = { client: "the client", workpaper: "another workpaper", decision: "a decision" };
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const clip = (s: string, n = 140) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const byRef = (a: WpIndexRow, b: WpIndexRow) => a.ref.localeCompare(b.ref, undefined, { numeric: true });
const answer = (chip: ChipId, d: QaData, summary: string, groups: QaGroup[]): QuickAnswer => {
  const kept = groups.filter((g) => g.lines.length);
  return { chip, question: chipLabel(chip), summary, groups: kept, empty: kept.length === 0, asOf: d.syncedAt };
};
export const chipLabel = (chip: ChipId) => Object.values(QUICK_CHIPS).flat().find((c) => c.id === chip)?.label ?? chip;

// ── engagement ──

/** Blocked workpapers (waiting on something, or needing input) grouped by what they wait on. */
export function whatsBlocking(d: QaData): QuickAnswer {
  const blocked = d.wp.filter((w) => !!w.waitingOn || w.status === "needs_input").sort(byRef);
  const line = (w: WpIndexRow): QaLine => {
    const item = d.attention.find((a) => a.tab === "blocked" && a.ref === w.ref);
    return {
      key: w.ref, text: `${w.ref} ${w.name}`, href: wpHref(d.id, w.ref),
      detail: w.waitingOn ? `Waits on ${w.waitingOn.label}${w.waitingOn.ref && w.waitingOn.ref !== w.waitingOn.label ? ` (${w.waitingOn.ref})` : ""}`
        : w.statusWhy ?? w.staleWhy ?? item?.title ?? w.statusLabel,
      also: w.waitingOn ? { label: w.waitingOn.kind === "client" ? "Open the document" : w.waitingOn.kind === "workpaper" ? `Open ${w.waitingOn.ref}` : "Open the decision", href: waitHref(d, w.waitingOn) } : undefined,
    };
  };
  const kinds: Array<[string, string]> = [["client", "Waiting on a client document"], ["workpaper", "Waiting on another workpaper"], ["decision", "Waiting on a decision"]];
  const groups: QaGroup[] = kinds.map(([k, title]) => ({ title, lines: blocked.filter((w) => w.waitingOn?.kind === k).map(line) }));
  const other = blocked.filter((w) => !w.waitingOn || !kinds.some(([k]) => k === w.waitingOn!.kind)).map(line);
  // Blocked items the engine lists that no blocked workpaper row carries.
  for (const i of d.attention.filter((a) => a.tab === "blocked" && !blocked.some((w) => w.ref === a.ref))) {
    other.push({ key: `item:${i.key}`, text: i.title, href: itemHref(i), detail: i.detail ?? undefined });
  }
  groups.push({ title: "Other reasons", lines: other });
  const n = blocked.length;
  const parts = kinds.map(([k]) => [k, blocked.filter((w) => w.waitingOn?.kind === k).length] as const).filter(([, c]) => c > 0).map(([k, c]) => `${c} on ${WAIT_WORD[k]}`);
  const summary = n === 0 && other.length === 0 ? "Nothing is blocked."
    : n === 0 ? `No workpaper is blocked; ${plural(other.length, "item")} in the Action center ${other.length === 1 ? "is" : "are"} marked blocked.`
    : `${plural(n, "workpaper")} ${n === 1 ? "is" : "are"} blocked${parts.length ? `: ${parts.join(", ")}` : ""}.`;
  return answer("blocking", d, summary, groups);
}

/** Approval items waiting on me. */
export function needsMyApproval(d: QaData): QuickAnswer {
  const mine = d.attention.filter((i) => i.tab === "approval" && waitsOnMe(i, d.me)).sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));
  const lines = mine.map((i): QaLine => ({ key: i.key, text: i.title, href: itemHref(i), detail: `${subkindLabel(i.subkind)}${i.ref ? ` · ${i.ref}` : ""} · waiting ${age(i.createdAt, d.now)}` }));
  return answer("approvals", d, mine.length ? `${plural(mine.length, "item")} ${mine.length === 1 ? "waits" : "wait"} on your approval.` : "Nothing waits on your approval.", [{ title: null, lines }]);
}

/** Client documents the engine lists as late, and outstanding ones whose due date has passed. */
export function lateDocuments(d: QaData, chip: ChipId = "late_docs"): QuickAnswer {
  const late = d.pbc.filter((p) => p.status === "late" || (p.status === "outstanding" && !!p.due && p.due.slice(0, 10) < d.today))
    .sort((a, b) => (a.due ?? "9999").localeCompare(b.due ?? "9999"));
  const lines = late.map((p): QaLine => ({
    key: p.id, text: `${p.ref} ${p.title}`, href: docHref(d.id, p.id),
    detail: `${p.due ? `Due ${fmtDate(p.due)}` : "No due date"} · ${p.status === "late" ? "late" : "past its due date (still listed as due)"}${p.neededBy.length ? ` · needed by ${p.neededBy.join(", ")}` : ""}`,
  }));
  return answer(chip, d, late.length ? `${plural(late.length, "client document")} ${late.length === 1 ? "is" : "are"} late.` : "No client document is late.", [{ title: null, lines }]);
}

/** Final of total per phase 00–07 and where the work is now. */
export function progressByPhase(d: QaData): QuickAnswer {
  const now = workPhase(d.wp);
  const lines = PHASES.filter((p) => Number(p.code) <= 7).map((p): QaLine => {
    const s = phaseSummary(d.wp.filter((w) => w.phase === p.code));
    return { key: p.code, text: `${p.code} ${p.name}`, href: phaseHref(d.id, p.code), badge: now === p.code ? "Now" : undefined,
      detail: s.total ? `${s.final} of ${s.total} final${s.needs ? ` · ${s.needs} need input` : ""}` : "No workpapers expected" };
  });
  const total = d.progress ?? { final: d.wp.filter((w) => w.status === "final").length, total: d.wp.length };
  const summary = !d.wp.length ? "No workpapers are listed yet."
    : `${now ? `Now: ${now} ${PHASES.find((p) => p.code === now)!.name}.` : "Every phase with work is final."} ${total.final} of ${total.total} workpapers final overall.`;
  return { ...answer("phases", d, summary, [{ title: null, lines: d.wp.length ? lines : [] }]) };
}

/** The same list as Overview's "What's next". */
export function whatsNext(d: QaData): QuickAnswer {
  const list = nextItems(d.attention, d.me);
  const lines = list.map((i): QaLine => ({
    key: i.key, text: i.title, href: itemHref(i),
    detail: `Waits on ${i.waitsOn.kind === "client" ? `the client${i.waitsOn.name ? ` (${i.waitsOn.name})` : ""}` : i.waitsOn.kind === "agent" ? "the agent" : i.waitsOn.name === d.me ? "you" : i.waitsOn.name ?? "someone"} · ${age(i.createdAt, d.now)}`,
  }));
  const more = d.attention.length - list.length;
  return answer("next", d, list.length ? `${plural(list.length, "item")} next${more > 0 ? ` (${more} more in the Action center)` : ""}.` : "Nothing waits on anyone right now.", [{ title: null, lines }]);
}

// ── workpaper ──
const ROLES = ["preparer", "reviewer", "partner", "eqr"] as const;
const ROLE_LABEL: Record<(typeof ROLES)[number], string> = { preparer: "Preparer", reviewer: "Reviewer", partner: "Partner", eqr: "Quality reviewer (EQR)" };
const TEAM_ROLE: Record<(typeof ROLES)[number], string> = { preparer: "staff", reviewer: "manager", partner: "engagement_partner", eqr: "eqr" };
const missingSignoffs = (w: WpIndexRow) => ROLES.filter((r) => { const m = w.signoffs[r]; return m !== null && !m.done; });
const noRow = (chip: ChipId, d: QaData, ref: string) => answer(chip, d, `${ref} is not in the engagement's workpaper index.`, []);

/** Why a workpaper is blocked: what it waits on, the engine's reason, staleness and blocked items on it. */
export function whyBlocked(d: QaData, ref: string): QuickAnswer {
  const w = d.wp.find((x) => x.ref === ref);
  if (!w) return noRow("why_blocked", d, ref);
  const lines: QaLine[] = [];
  if (w.waitingOn) lines.push({ key: "wait", text: `Waits on ${WAIT_WORD[w.waitingOn.kind] ?? w.waitingOn.kind}: ${w.waitingOn.label}`, href: waitHref(d, w.waitingOn), detail: w.waitingOn.ref });
  if (w.statusWhy && w.statusWhy !== w.waitingOn?.label) lines.push({ key: "why", text: w.statusWhy, href: wpHref(d.id, w.ref), detail: `Status: ${w.statusLabel}` });
  if (w.stale) lines.push({ key: "stale", text: `Out of date: ${w.staleWhy ?? "an input changed after it was rendered"}`, href: wpHref(d.id, w.ref) });
  for (const i of d.attention.filter((a) => a.tab === "blocked" && a.ref === w.ref)) lines.push({ key: `item:${i.key}`, text: i.title, href: itemHref(i), detail: i.detail ?? undefined });
  const blocked = w.status === "needs_input" || !!w.waitingOn || lines.length > 0;
  const summary = !blocked ? `Nothing is blocking ${w.ref} (status: ${w.statusLabel}).`
    : lines.length ? `${w.ref} (status: ${w.statusLabel}) is held up by ${plural(lines.length, "thing")}.` : `${w.ref} (status: ${w.statusLabel}) is blocked, with no reason published.`;
  return answer("why_blocked", d, summary, [{ title: null, lines }]);
}

/** What is left on a workpaper: steps not done, sign-offs missing by role, open questions, notes and items. */
export function whatsLeft(d: QaData, ref: string): QuickAnswer {
  const w = d.wp.find((x) => x.ref === ref);
  if (!w) return noRow("left", d, ref);
  const href = wpHref(d.id, w.ref);
  const steps: QaLine[] = w.steps.total > w.steps.done ? [{ key: "steps", text: `${w.steps.total - w.steps.done} of ${w.steps.total} steps not done`, href }] : [];
  const signs = missingSignoffs(w).map((r): QaLine => ({ key: `so:${r}`, text: `${ROLE_LABEL[r]} sign-off`, href }));
  const qs = d.messages.filter((m) => m.scope === "workpaper" && m.ref === w.ref && m.kind === "question" && (m.status === "open" || m.status === "answered"));
  const open: QaLine[] = qs.map((m) => ({ key: `q:${m.id}`, text: clip(m.body), href, detail: m.status === "answered" ? "Answered — waiting to be accepted" : `Open question${m.to ? ` to ${m.to === d.me ? "you" : m.to}` : ""}` }));
  if (w.counts.notes > 0) open.push({ key: "notes", text: plural(w.counts.notes, "open review note"), href });
  const items = d.attention.filter((a) => a.ref === w.ref && !(a.tab === "question" && qs.some((m) => m.id === String(a.thread ?? ""))))
    .map((i): QaLine => ({ key: `item:${i.key}`, text: i.title, href: itemHref(i), detail: subkindLabel(i.subkind) }));
  const n = steps.length + signs.length + open.length + items.length;
  const summary = n === 0 ? (w.status === "final" ? `Nothing is left: ${w.ref} is final.` : `Nothing is listed as left on ${w.ref} (status: ${w.statusLabel}).`)
    : `${w.ref} (status: ${w.statusLabel}): ${[steps.length ? plural(w.steps.total - w.steps.done, "step") : "", signs.length ? plural(signs.length, "sign-off") : "", open.length ? plural(open.length, "open question or note", "open questions or notes") : "", items.length ? plural(items.length, "action item") : ""].filter(Boolean).join(", ")} left.`;
  return answer("left", d, summary, [
    { title: "Steps", lines: steps }, { title: "Sign-offs missing", lines: signs }, { title: "Open questions and notes", lines: open }, { title: "Action items", lines: items },
  ]);
}

/** The next sign-off in order (preparer → reviewer → partner → EQR when required) and who it waits on. */
export function whoSignsNext(d: QaData, ref: string): QuickAnswer {
  const w = d.wp.find((x) => x.ref === ref);
  if (!w) return noRow("signs_next", d, ref);
  const who = missingSignoffs(w).map((r) => {
    const item = d.attention.find((a) => a.tab === "approval" && a.subkind === "signoff" && a.ref === w.ref && a.waitsOn.kind === "person"
      && String(a.action.payload.role ?? r) === r);
    const team = d.team.find((t) => t.role === TEAM_ROLE[r])?.person ?? null;
    const name = item?.waitsOn.name ?? (r === "preparer" ? w.owner ?? team : team);
    const src = item ? "A sign-off item waits on them" : !name ? undefined : r === "preparer" && w.owner ? "The workpaper's owner" : "From the engagement team";
    return { r, item, name, src, said: name ? (name === d.me ? `you (${name})` : name) : "no one assigned" };
  });
  const lines = who.map((x, n): QaLine => ({ key: x.r, text: `${ROLE_LABEL[x.r]}: ${x.said}`, href: x.item ? itemHref(x.item) : wpHref(d.id, w.ref), badge: n === 0 ? "Next" : undefined, detail: x.src }));
  const summary = !who.length ? `Every sign-off on ${w.ref} is done.` : `${ROLE_LABEL[who[0].r]} signs next: ${who[0].said}.`;
  return answer("signs_next", d, summary, [{ title: null, lines }]);
}

// ── client documents ──

const CHECK_WORDS: Array<[keyof PbcIndexRow["checks"], string]> = [["read", "read"], ["verified", "verified"], ["filed", "filed"]];
/** Documents whose processing hit an issue, with the engine's issue and which checks are not done. */
export function failedProcessing(d: QaData): QuickAnswer {
  const bad = d.pbc.filter((p) => p.status === "issue" || !!p.issue).sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  const lines = bad.map((p): QaLine => {
    const notYet = CHECK_WORDS.filter(([k]) => !p.checks[k]).map(([, w]) => w);
    return { key: p.id, text: `${p.ref} ${p.title}`, href: docHref(d.id, p.id), detail: `${p.issue ?? "An issue is recorded"} · ${notYet.length ? `not yet ${notYet.join(", ")}` : "read, verified and filed"}` };
  });
  return answer("pbc_failed", d, bad.length ? `${plural(bad.length, "client document")} failed processing.` : "No client document failed processing.", [{ title: null, lines }]);
}

/** Outstanding (and late) documents grouped by type. */
export function outstandingByType(d: QaData): QuickAnswer {
  const out = d.pbc.filter((p) => p.status === "outstanding" || p.status === "late");
  const types = [...new Set(out.map((p) => p.docTypeLabel))].sort();
  const groups = types.map((t): QaGroup => {
    const rows = out.filter((p) => p.docTypeLabel === t).sort((a, b) => (a.due ?? "9999").localeCompare(b.due ?? "9999"));
    return { title: `${t} · ${rows.length}`, lines: rows.map((p) => ({ key: p.id, text: `${p.ref} ${p.title}`, href: docHref(d.id, p.id),
      detail: `${p.status === "late" ? "Late" : "Due"}${p.due ? ` ${fmtDate(p.due)}` : ""}` })) };
  });
  return answer("pbc_outstanding", d, out.length ? `${plural(out.length, "client document")} outstanding in ${plural(types.length, "type")}.` : "Nothing is outstanding from the client.", groups);
}

/** The answer for a chip (the workpaper chips need the workpaper's ref). */
export function quickAnswer(chip: ChipId, d: QaData, ref: string | null = null): QuickAnswer {
  switch (chip) {
    case "blocking": return whatsBlocking(d);
    case "approvals": return needsMyApproval(d);
    case "late_docs": return lateDocuments(d, "late_docs");
    case "phases": return progressByPhase(d);
    case "next": return whatsNext(d);
    case "why_blocked": return whyBlocked(d, ref ?? "");
    case "left": return whatsLeft(d, ref ?? "");
    case "signs_next": return whoSignsNext(d, ref ?? "");
    case "pbc_late": return lateDocuments(d, "pbc_late");
    case "pbc_failed": return failedProcessing(d);
    case "pbc_outstanding": return outstandingByType(d);
  }
}
